// ABOUTME: In-app login screen — Kratos login flow split into method sections (OIDC / password / code).
// ABOUTME: Each section is its own FlowForm sub-form so the code method carries its own identifier.

import { useCallback, useEffect, useMemo, useRef } from "react"
import { Link, useSearchParams } from "react-router"
import type { LoginFlow, UiNode, UpdateLoginFlowBody } from "@ory/client"
import { useTranslation } from "react-i18next"
import { ory } from "@/model/auth/ory"
import { useKratosFlow } from "@/features/auth/useKratosFlow"
import { FlowForm } from "@/features/auth/FlowForm"
import {
  buildFlowBody,
  inputAttrs,
  isMethodButton,
  kratosMessage,
  messageClass,
  seedValues,
} from "@/features/auth/nodeMapping"
import { safeReturnTo } from "@/features/auth/redirectGuard"
import { AUTH_LINK_CLASS, SECTION_LABEL_CLASS } from "@/features/auth/authStyles"
import { AuthCard, AuthDivider } from "./AuthCard"
import { AuthFlowError } from "./AuthFlowError"
import { AuthLoading } from "./AuthLoading"
import { asFlowBody } from "@/features/auth/flowBody"

// A sub-flow scoped to a subset of nodes; form-level messages are rendered once by the page.
const flowOf = (nodes: UiNode[]): { ui: { nodes: UiNode[] } } => ({ ui: { nodes } })

export const LoginPage = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get("return_to")
  const withReturn = (path: string) => (returnTo ? `${path}?return_to=${encodeURIComponent(returnTo)}` : path)
  // OIDC round-trips leave the SPA entirely; without an explicit return_to Kratos
  // falls back to its default_browser_return_url, which on the split-host envs
  // (beta/alpha vs the shared prod Kratos) is a DIFFERENT host than this app.
  const kratosReturnTo = new URL(safeReturnTo(returnTo), window.location.origin).href

  const { flow, submit, submitting, failed, restart } = useKratosFlow<LoginFlow>({
    createFlow: () => ory.createBrowserLoginFlow({ refresh: false, returnTo: kratosReturnTo }),
    getFlow: (id) => ory.getLoginFlow({ id }),
    updateFlow: (id, body) =>
      ory.updateLoginFlow({ flow: id, updateLoginFlowBody: asFlowBody<UpdateLoginFlowBody>(body) }),
  })

  // ?provider=google|apple — the native app opens this URL in a Custom Tab to run
  // OIDC in a real browser (Google blocks it inside the app's WebView); auto-submit
  // the provider so the user doesn't have to pick it a second time.
  const providerParam = searchParams.get("provider")
  const autoSubmitted = useRef(false)
  // Stable so the effect below can list it: providerParam is all it closes over. As a fresh closure
  // per render it forced a suppression, and an inline react-hooks disable makes the React Compiler
  // skip the whole page.
  const providerNodeOf = useCallback(
    (f: LoginFlow) => f.ui.nodes.find((n) => n.group === "oidc" && String(inputAttrs(n)?.value) === providerParam),
    [providerParam],
  )
  useEffect(() => {
    if (!flow || !providerParam || autoSubmitted.current || submitting) return
    const node = providerNodeOf(flow)
    if (!node) return // unknown/disabled provider — fall through to the normal form
    autoSubmitted.current = true
    submit(buildFlowBody(flow.ui.nodes, node, seedValues(flow.ui.nodes)))
    // The autoSubmitted ref keeps this one-shot whatever the list says, so naming submit and
    // providerNodeOf costs nothing.
  }, [flow, providerParam, submitting, providerNodeOf, submit])

  // Keep the spinner up while the auto-submitted OIDC round-trip is in flight —
  // unless Kratos came back with messages (e.g. an account-linking prompt) or the
  // provider param didn't match any node.
  const autoOidcPending =
    Boolean(providerParam) && !failed && !flow?.ui.messages?.length && (!flow || providerNodeOf(flow) !== undefined)

  // Build the per-method sub-flows once per flow identity. New object/array references on every
  // render would re-fire FlowForm's seed effect and wipe the user's typed email/password/code.
  const sections = useMemo(() => {
    if (!flow) return null
    const nodes = flow.ui.nodes
    const hiddenNodes = nodes.filter((n) => inputAttrs(n)?.type === "hidden") // csrf etc.
    const oidcNodes = nodes.filter((n) => n.group === "oidc")
    const passwordNodes = nodes.filter((n) => n.group === "default" || n.group === "password")
    const codeNodes = nodes.filter((n) => n.group === "default" || n.group === "code")
    return {
      hasOidc: oidcNodes.length > 0,
      hasPassword: nodes.some((n) => n.group === "password"),
      hasCode: codeNodes.some((n) => n.group === "code" && isMethodButton(n)),
      // Once a code has been sent, Kratos adds a `code` input — show only that step.
      codeSent: nodes.some((n) => inputAttrs(n)?.name === "code"),
      oidcFlow: flowOf([...hiddenNodes, ...oidcNodes]),
      passwordFlow: flowOf(passwordNodes),
      codeFlow: flowOf(codeNodes),
    }
  }, [flow])

  if (failed)
    return (
      <AuthFlowError
        title={t("auth.signInTitle")}
        onRetry={restart}
      />
    )
  if (!flow || !sections || autoOidcPending) return <AuthLoading />

  const messages = (flow.ui.messages ?? []).map((m) => (
    <p
      key={m.id}
      className={`text-sm break-words ${messageClass(m.type)}`}
    >
      {kratosMessage(m, t)}
    </p>
  ))

  if (sections.codeSent) {
    return (
      <AuthCard
        title={t("auth.signInTitle")}
        footer={
          <button
            type="button"
            onClick={restart}
            className={AUTH_LINK_CLASS}
          >
            {t("auth.backToLogin")}
          </button>
        }
      >
        {messages}
        <p className={SECTION_LABEL_CLASS}>{t("auth.oneTimeCode")}</p>
        <FlowForm
          flow={sections.codeFlow}
          submitting={submitting}
          onSubmit={submit}
        />
      </AuthCard>
    )
  }

  return (
    <AuthCard
      title={t("auth.signInTitle")}
      footerSplit
      footer={
        <>
          <Link
            to={withReturn("/auth/recovery")}
            className={AUTH_LINK_CLASS}
          >
            {t("auth.forgotPassword")}
          </Link>
          <Link
            to={withReturn("/auth/registration")}
            className={AUTH_LINK_CLASS}
          >
            {t("auth.signUp")}
          </Link>
        </>
      }
    >
      {messages}

      {sections.hasOidc && (
        <FlowForm
          flow={sections.oidcFlow}
          submitting={submitting}
          onSubmit={submit}
        />
      )}

      {sections.hasOidc && sections.hasPassword && <AuthDivider label={t("auth.or")} />}

      {sections.hasPassword && (
        <FlowForm
          flow={sections.passwordFlow}
          submitting={submitting}
          onSubmit={submit}
        />
      )}

      {sections.hasCode && <AuthDivider label={t("auth.or")} />}

      {sections.hasCode && (
        <>
          <p className={SECTION_LABEL_CLASS}>{t("auth.oneTimeCode")}</p>
          <FlowForm
            flow={sections.codeFlow}
            submitting={submitting}
            onSubmit={submit}
          />
        </>
      )}
    </AuthCard>
  )
}
